import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../api';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Button,
  Chip,
  Select,
  MenuItem,
  FormControl,
  Stack,
  Container,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';

const PRIMARY = '#6366f1';

function ReviewPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [duplicates, setDuplicates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [duplicateAction, setDuplicateAction] = useState('skip');

  useEffect(() => {
    console.log('=== ReviewPage Debug ===');
    console.log('location.state:', JSON.stringify(location.state, null, 2));

    if (!location.state) {
      console.log('ERROR: No location.state found');
      navigate('/');
      return;
    }

    if (!location.state.transactions) {
      console.log('ERROR: No transactions in location.state');
      navigate('/');
      return;
    }

    const allTransactions = location.state.transactions;
    const duplicateInfo = location.state.duplicates;
    const fileResults = location.state.fileResults || [];
    const filesProcessed = location.state.filesProcessed || 1;

    console.log('All transactions count:', allTransactions?.length);
    console.log('Duplicate info:', duplicateInfo);
    console.log('Files processed:', filesProcessed);
    console.log('File results:', fileResults);

    if (duplicateInfo && duplicateInfo.newTransactions) {
      const newTxns = duplicateInfo.newTransactions;
      const dupTxns = duplicateInfo.duplicateTransactions || [];
      console.log('Using duplicate info:');
      console.log('  New transactions:', newTxns.length);
      console.log('  Duplicate transactions:', dupTxns.length);
      setTransactions(newTxns);
      setDuplicates(dupTxns);
    } else {
      console.log('No duplicate info, using all transactions:', allTransactions.length);
      setTransactions(allTransactions);
      setDuplicates([]);
    }

    fetchCategories();
  }, [location, navigate]);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/api/categories');
      setCategories(response.data.categories);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  const handleCategoryChange = (index, newCategory, isDuplicate = false) => {
    if (isDuplicate) {
      const updated = [...duplicates];
      updated[index].category = newCategory;
      setDuplicates(updated);
    } else {
      const updated = [...transactions];
      updated[index].category = newCategory;
      setTransactions(updated);
    }
  };

  const handleSaveAll = async () => {
    setSaving(true);
    setError(null);

    try {
      let transactionsToSave = [...transactions];
      if (duplicateAction === 'include' && duplicates.length > 0) {
        transactionsToSave = [...transactions, ...duplicates];
      }
      await api.post('/api/transactions/save', { transactions: transactionsToSave });
      setSuccess(true);
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save transactions');
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString();
  };

  const formatAmount = (amount) => {
    const absAmount = Math.abs(parseFloat(amount));
    return amount < 0 ? `-$${absAmount.toFixed(2)}` : `$${absAmount.toFixed(2)}`;
  };

  const saveCount =
    duplicateAction === 'skip' ? transactions.length : transactions.length + duplicates.length;

  if (success) {
    return (
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Paper
          elevation={0}
          sx={{ p: 6, textAlign: 'center', boxShadow: '0 0 0 1px rgba(255,255,255,0.05), 0 2px 8px rgba(0,0,0,0.3)', borderRadius: 3, maxWidth: 400 }}
        >
          <CheckCircleOutlineIcon sx={{ fontSize: 72, color: 'success.main', mb: 2 }} />
          <Typography variant="h6" fontWeight={700} color="success.main" mb={1}>
            Transactions Saved Successfully!
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Redirecting to dashboard...
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="lg">
        <Typography variant="h5" fontWeight={700} mb={0.5} color="text.primary">
          Review Transactions
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Review and edit categories before saving
        </Typography>

        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* File Processing Summary */}
        {location.state?.filesProcessed > 1 && (
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              mb: 3,
              border: '1px solid',
              borderColor: 'rgba(34,197,94,0.35)',
              borderRadius: 2,
              bgcolor: 'rgba(34,197,94,0.06)',
            }}
          >
            <Stack direction="row" alignItems="center" gap={1.5} mb={location.state.fileResults ? 1.5 : 0}>
              <FolderOpenIcon sx={{ color: 'success.main', fontSize: 22 }} />
              <Typography variant="body2" fontWeight={600} color="success.light">
                Processed {location.state.filesProcessed} files successfully
              </Typography>
            </Stack>
            {location.state.fileResults && (
              <Stack gap={0.75} pt={1.5} sx={{ borderTop: '1px solid rgba(34,197,94,0.2)' }}>
                {location.state.fileResults.map((result, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      px: 1.5,
                      py: 1,
                      bgcolor: 'rgba(255,255,255,0.04)',
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="body2" fontWeight={500}>
                      {result.filename}
                    </Typography>
                    <Chip
                      label={`${result.transactionCount} transaction${result.transactionCount !== 1 ? 's' : ''}`}
                      size="small"
                      sx={{ bgcolor: 'rgba(39,174,96,0.1)', color: 'success.dark', fontWeight: 600 }}
                    />
                  </Box>
                ))}
              </Stack>
            )}
          </Paper>
        )}

        {/* Duplicate Detection Summary */}
        {duplicates.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              mb: 3,
              border: '2px solid',
              borderColor: 'rgba(245,158,11,0.4)',
              borderRadius: 2,
              bgcolor: 'rgba(245,158,11,0.06)',
            }}
          >
            <Stack direction="row" alignItems="center" gap={1.5} mb={2}>
              <WarningAmberIcon sx={{ color: 'warning.main', fontSize: 24 }} />
              <Typography variant="body2" fontWeight={600} color="warning.main">
                Found {duplicates.length} duplicate transaction{duplicates.length > 1 ? 's' : ''} (already in your history)
              </Typography>
            </Stack>

            <Stack direction="row" alignItems="center" gap={3} flexWrap="wrap">
              <Stack direction="row" alignItems="center" gap={1}>
                <input
                  type="radio"
                  name="duplicateAction"
                  value="skip"
                  id="dup-skip"
                  checked={duplicateAction === 'skip'}
                  onChange={(e) => setDuplicateAction(e.target.value)}
                  style={{ cursor: 'pointer', width: 16, height: 16 }}
                />
                <label htmlFor="dup-skip" style={{ cursor: 'pointer', fontWeight: 500, fontSize: 14 }}>
                  Skip duplicates (recommended)
                </label>
              </Stack>
              <Stack direction="row" alignItems="center" gap={1}>
                <input
                  type="radio"
                  name="duplicateAction"
                  value="include"
                  id="dup-include"
                  checked={duplicateAction === 'include'}
                  onChange={(e) => setDuplicateAction(e.target.value)}
                  style={{ cursor: 'pointer', width: 16, height: 16 }}
                />
                <label htmlFor="dup-include" style={{ cursor: 'pointer', fontWeight: 500, fontSize: 14 }}>
                  Include duplicates anyway
                </label>
              </Stack>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setShowDuplicates(!showDuplicates)}
                sx={{
                  borderColor: 'rgba(245,158,11,0.4)',
                  color: 'warning.main',
                  fontWeight: 600,
                  '&:hover': { borderColor: 'rgba(245,158,11,0.6)', bgcolor: 'rgba(245,158,11,0.08)' },
                }}
              >
                {showDuplicates ? 'Hide' : 'View'} Duplicates
              </Button>
            </Stack>
          </Paper>
        )}

        {/* New Transactions Table */}
        <Box mb={3}>
          <Typography variant="subtitle1" fontWeight={700} mb={1.5} color="text.primary">
            New Transactions ({transactions.length})
          </Typography>
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{ boxShadow: '0 0 0 1px rgba(255,255,255,0.05), 0 2px 8px rgba(0,0,0,0.3)', borderRadius: 2 }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Merchant</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Amount</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.map((transaction, index) => (
                  <TableRow key={index} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary', fontSize: 13 }}>
                      {formatDate(transaction.date)}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{transaction.merchant}</TableCell>
                    <TableCell
                      sx={{
                        maxWidth: 280,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        color: 'text.secondary',
                        fontSize: 13,
                      }}
                    >
                      {transaction.description}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        color: transaction.amount < 0 ? 'success.main' : 'error.main',
                      }}
                    >
                      {formatAmount(transaction.amount)}
                    </TableCell>
                    <TableCell sx={{ minWidth: 160 }}>
                      <FormControl size="small" fullWidth>
                        <Select
                          value={transaction.category || ''}
                          onChange={(e) => handleCategoryChange(index, e.target.value, false)}
                          sx={{ fontSize: 13 }}
                        >
                          {categories.map((cat) => (
                            <MenuItem key={cat.id} value={cat.name} sx={{ fontSize: 13 }}>
                              {cat.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Duplicate Transactions (collapsible via Dialog) */}
        {duplicates.length > 0 && (
          <Dialog
            open={showDuplicates}
            onClose={() => setShowDuplicates(false)}
            maxWidth="lg"
            fullWidth
          >
            <DialogTitle sx={{ fontWeight: 700, color: 'warning.main' }}>
              Duplicate Transactions ({duplicates.length})
            </DialogTitle>
            <DialogContent dividers>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Merchant</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Amount</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {duplicates.map((transaction, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary', fontSize: 13 }}>
                          {formatDate(transaction.date)}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 500 }}>{transaction.merchant}</TableCell>
                        <TableCell
                          sx={{
                            maxWidth: 280,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: 'text.secondary',
                            fontSize: 13,
                          }}
                        >
                          {transaction.description}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            color: transaction.amount < 0 ? 'success.main' : 'error.main',
                          }}
                        >
                          {formatAmount(transaction.amount)}
                        </TableCell>
                        <TableCell sx={{ minWidth: 160 }}>
                          <FormControl size="small" fullWidth>
                            <Select
                              value={transaction.category || ''}
                              onChange={(e) => handleCategoryChange(index, e.target.value, true)}
                              disabled={duplicateAction === 'skip'}
                              sx={{ fontSize: 13 }}
                            >
                              {categories.map((cat) => (
                                <MenuItem key={cat.id} value={cat.name} sx={{ fontSize: 13 }}>
                                  {cat.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={() => setShowDuplicates(false)} sx={{ color: 'text.secondary' }}>
                Close
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {/* Save Action */}
        <Stack alignItems="center" mt={2}>
          <Button
            variant="contained"
            size="large"
            onClick={handleSaveAll}
            disabled={saving}
            sx={{
              px: 6,
              py: 1.5,
              bgcolor: 'success.main',
              fontWeight: 700,
              fontSize: 16,
              borderRadius: 2,
              '&:hover': { bgcolor: 'success.dark' },
              '&:disabled': { bgcolor: 'grey.400' },
            }}
          >
            {saving
              ? 'Saving...'
              : `Save ${saveCount} Transaction${saveCount !== 1 ? 's' : ''}`}
          </Button>
        </Stack>
      </Container>
    </Box>
  );
}

export default ReviewPage;
